/**
 * The Start popup's prefill, from the owner's Billing details (the umbrella spec's prefill table),
 * and Start over's, from the names the service kept from the last attempt. An empty prefill leaves
 * the field empty for the owner to type.
 *
 * Billing is taken as a narrow structural type (Decision 18): the slice reads `window` at module
 * scope, so it is imported as a type only and jest never loads it.
 */
import type { BillingPrefs } from '@/store/slices/userPreferences';

import type { ContractStatus } from './api/contract';

export type BillingNames = Pick<
  BillingPrefs,
  | 'accountType'
  | 'accountHolderFirstName'
  | 'accountHolderLastName'
  | 'businessLegalName'
  | 'beneficialOwnerFirstName'
  | 'beneficialOwnerLastName'
>;

export type Prefill = { operator: string; signatoryShown: boolean; signatory: string };

/** A value trimmed. The slice casts the stored JSON, so a record saved before a field existed has no
 *  value for it at all: anything but a string is ''. */
const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** The non-empty parts, trimmed, joined with one space. */
const join = (first: unknown, last: unknown): string =>
  [text(first), text(last)].filter((part) => part !== '').join(' ');

/** The umbrella's prefill table. `null` billing is the last row. Values trimmed; missing values give ''. */
export const prefillFrom = (billing: BillingNames | null): Prefill => {
  if (billing?.accountType === 'individual') {
    // The operator signs: no signatory field, and the service uses the operator name.
    return {
      operator: join(billing.accountHolderFirstName, billing.accountHolderLastName),
      signatoryShown: false,
      signatory: '',
    };
  }
  const signatory = join(billing?.beneficialOwnerFirstName, billing?.beneficialOwnerLastName);
  if (billing?.accountType === 'business') {
    return { operator: text(billing.businessLegalName), signatoryShown: true, signatory };
  }
  // Unset, a type the app does not know, or no billing at all.
  return { operator: '', signatoryShown: true, signatory };
};

/** Start over: the service's last names over the billing prefill (each only when non-null and
 *  non-empty after trim). `signatoryShown` is unchanged. */
export const restartPrefill = (status: ContractStatus, billing: BillingNames | null): Prefill => {
  const base = prefillFrom(billing);
  return {
    operator: text(status.operator_name) || base.operator,
    signatoryShown: base.signatoryShown,
    signatory: text(status.signatory_name) || base.signatory,
  };
};
